// One campaign: progress, where the performers came from, store clicks and
// opens, the reviews they posted, and cancel / restart.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { useCanEdit } from '@/components/EditOnly';
import { useToastStore } from '@/store/toast';
import { cancelCampaign, decideAutoOrder, getCampaign, getCampaignTasks, restartCampaign } from '@/api/asoMarket';
import {
  AppLogo, CANCEL_REASONS, Card, Flags, Notice, Progress, Stars, StatusPill, TypeBadge, fmtDate, hoursText, orderWords, usd,
} from './ui';

const TASK_STATUS: Record<number, [string, string]> = {
  1: ['In work', 'text-info'],
  2: ['Checking', 'text-warning'],
  3: ['Done', 'text-success'],
  4: ['Cancelled', 'text-content-subtle'],
};

export function CampaignModal({ id, onClose }: { id: number | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToastStore((s) => s.push);
  const canEdit = useCanEdit();
  const [tab, setTab] = useState<'overview' | 'reviews' | 'texts'>('overview');
  const [taskPage, setTaskPage] = useState(0);
  const detail = useQuery({ queryKey: ['aso-market', 'campaign', id], queryFn: () => getCampaign(id!), enabled: id != null });
  const c = detail.data?.campaign;
  const tasks = useQuery({
    queryKey: ['aso-market', 'tasks', id, taskPage],
    queryFn: () => getCampaignTasks(id!, taskPage * 50),
    enabled: id != null && tab === 'reviews' && !!c?.remote_id,
  });

  const act = useMutation({
    mutationFn: (what: 'cancel' | 'restart') => (what === 'cancel' ? cancelCampaign(id!) : restartCampaign(id!)),
    onSuccess: (_d, what) => {
      toast(what === 'cancel' ? 'Campaign cancelled; unspent money goes back to the balance' : 'Campaign restarted');
      qc.invalidateQueries({ queryKey: ['aso-market'] });
    },
    onError: (e: Error) => toast(e.message),
  });

  const decide = useMutation({
    mutationFn: (what: 'approve' | 'skip') => decideAutoOrder(id!, what),
    onSuccess: (_d, what) => {
      toast(what === 'approve' ? 'Approved: the order goes out within a minute' : 'Order skipped');
      qc.invalidateQueries({ queryKey: ['aso-market'] });
    },
    onError: (e: Error) => toast(e.message),
  });

  const title = c ? (c.app_name || c.package_name) : 'Campaign';
  const cost = c && (c.price ?? c.quoted_price) != null ? (c.price ?? c.quoted_price)! * c.total : null;
  const geo = detail.data?.geo;
  const activity = detail.data?.activity;
  const clicks = (activity?.clickData ?? []).reduce((s, d) => s + d.count, 0);
  const opens = (activity?.openingData ?? []).reduce((s, d) => s + d.count, 0);
  const open = c && (c.status === 10 || c.status === 20);

  return (
    <Modal isOpen={id != null} onClose={onClose} title={title} size="4xl">
      {detail.isLoading && <p className="text-sm text-content-subtle">Loading the campaign from ASO.Market…</p>}
      {detail.isError && <Notice tone="danger">{(detail.error as Error).message}</Notice>}
      {c && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start gap-4">
            <AppLogo src={c.app_logo_url} name={title} size={56} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <TypeBadge campaign={c} />
                <StatusPill campaign={c} />
                {c.env === 'dev' && <span className="text-xs px-2 py-0.5 rounded bg-warning-soft text-warning">dev stand</span>}
              </div>
              <a href={c.application_url} target="_blank" rel="noreferrer" className="mt-1 block text-sm font-mono text-content-muted hover:text-primary truncate">{c.package_name} ↗</a>
              <div className="text-xs text-content-subtle mt-0.5">
                {c.remote_id ? `ASO.Market #${c.remote_id}` : 'Not on ASO.Market'} · {c.state === 'planned' || c.state === 'held' ? 'queued' : 'ordered'} {fmtDate(c.remote_created_at ?? c.created_at)}
                {c.auto ? ' automatically by the release plan' : c.created_by_name || c.created_by_email ? ` by ${c.created_by_name || c.created_by_email}` : c.created_by ? '' : ' in their cabinet'}
              </div>
            </div>
            {canEdit && (
              <div className="flex gap-2">
                {c.available_for_restart && !open && <Button size="sm" variant="secondary" loading={act.isPending && act.variables === 'restart'} onClick={() => act.mutate('restart')}>Restart</Button>}
                {c.state === 'held' && <Button size="sm" loading={decide.isPending && decide.variables === 'approve'} onClick={() => decide.mutate('approve')}>Order anyway</Button>}
                {(c.state === 'held' || c.state === 'planned') && <Button size="sm" variant="secondary" loading={decide.isPending && decide.variables === 'skip'} onClick={() => decide.mutate('skip')}>Skip</Button>}
                {open && <Button size="sm" variant="danger" loading={act.isPending && act.variables === 'cancel'} onClick={() => { if (confirm('Cancel this campaign? Tasks already done stay paid.')) act.mutate('cancel'); }}>Cancel campaign</Button>}
              </div>
            )}
          </div>

          {c.message && c.state !== 'created' && <Notice tone={c.state === 'unknown' || c.state === 'held' || c.state === 'planned' ? 'warning' : c.state === 'skipped' ? 'info' : 'danger'}>{c.message}</Notice>}
          {c.state === 'planned' && !c.message && <Notice tone="info">Queued: the review texts are written and the order goes to ASO.Market within a few minutes.</Notice>}
          {c.status === 10 && <Notice tone="warning">Waiting for payment: top up the balance and ASO.Market starts it.</Notice>}
          {c.status === 100 && c.cancellation_reason && <Notice tone="info">{CANCEL_REASONS[c.cancellation_reason] ?? `Cancelled (code ${c.cancellation_reason})`}</Notice>}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Cost" value={usd(cost)} sub={c.price != null ? `${usd(c.price, 3)} per task` : undefined} />
            <Stat label="Pace" value={`1 / ${hoursText(c.interval_hours)}`} sub={`≈ ${hoursText(c.total * c.interval_hours)} in all`} />
            <Stat label="Store clicks" value={activity ? String(clicks) : '—'} sub={activity ? `${opens} app opens` : undefined} />
            <Stat label="Refunded" value={String(Math.round(c.refunded))} sub={`${Math.round(c.canceled)} tasks cancelled`} />
          </div>

          <Card className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Progress</span>
              <span className="text-xs text-content-subtle">synced {fmtDate(c.synced_at)}</span>
            </div>
            <Progress c={c} size="lg" />
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-content-muted">
              <span>{orderWords(c).what}{orderWords(c).how ? ` ${orderWords(c).how}` : ''}</span>
              <span>Countries: <Flags codes={c.countries} max={12} /></span>
              {detail.data?.live?.is_high_risk && <span>high-risk</span>}
              {detail.data?.live?.no_guarantee && <span>no guarantee</span>}
              {detail.data?.live?.require_in_app_screenshots && <span>in-app screenshot</span>}
              {detail.data?.live?.share_limit != null && <span>≤{Math.round(detail.data.live.share_limit * 100)}% per country</span>}
            </div>
          </Card>

          <div className="flex gap-1 border-b border-border">
            {([['overview', 'Performers'], ['reviews', 'Tasks & reviews'], ...(c.review_texts.length ? [['texts', `Our texts (${c.review_texts.length})`]] : [])] as [typeof tab, string][]).map(([k, l]) => (
              <button key={k} type="button" onClick={() => setTab(k)}
                className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === k ? 'border-primary text-content' : 'border-transparent text-content-muted hover:text-content'}`}>{l}</button>
            ))}
          </div>

          {tab === 'overview' && (
            geo && geo.countries.length > 0 ? (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-content-subtle"><th className="py-1.5 font-normal">Performer country</th><th className="font-normal text-right">Assigned</th><th className="font-normal text-right">In work</th><th className="font-normal text-right">Checking</th><th className="font-normal text-right">Done</th></tr></thead>
                <tbody className="divide-y divide-border">
                  {geo.countries.map((g) => (
                    <tr key={g.country.code}>
                      <td className="py-1.5">{g.country.flag} {g.country.name}</td>
                      <td className="text-right tabular-nums">{g.assignedCount ?? 0}</td>
                      <td className="text-right tabular-nums">{g.inProgressCount ?? 0}</td>
                      <td className="text-right tabular-nums">{g.validationCount ?? 0}</td>
                      <td className="text-right tabular-nums font-medium">{g.finishedCount ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="text-sm text-content-subtle">No performer has taken a task yet.</p>
          )}

          {tab === 'reviews' && (
            <div className="space-y-2">
              {tasks.isLoading && <p className="text-sm text-content-subtle">Loading tasks…</p>}
              {tasks.data?.items.length === 0 && <p className="text-sm text-content-subtle">No tasks yet.</p>}
              {tasks.data?.items.map((t) => {
                const [label, tone] = TASK_STATUS[t.status] ?? [`#${t.status}`, ''];
                return (
                  <div key={t.id} className="p-3 rounded-lg border border-border">
                    <div className="flex items-center justify-between gap-3 text-xs">
                      <span className="flex items-center gap-2">
                        {t.review?.rating ? <Stars value={t.review.rating} size="sm" /> : null}
                        <span className="text-content-muted">{t.review?.nickname ?? `Task #${t.id}`}</span>
                      </span>
                      <span className="flex items-center gap-3">
                        <span className={tone}>{label}</span>
                        <span className="text-content-subtle">{fmtDate(t.review?.postedAt ?? t.createdAt)}</span>
                        <span className="tabular-nums text-content-muted">{usd(t.price, 3)}</span>
                      </span>
                    </div>
                    {t.review?.text && <p className="mt-1.5 text-sm text-content">{t.review.text}</p>}
                  </div>
                );
              })}
              {tasks.data && tasks.data.total > 50 && (
                <div className="flex justify-end gap-2 text-sm">
                  <Button size="sm" variant="secondary" disabled={taskPage === 0} onClick={() => setTaskPage(taskPage - 1)}>←</Button>
                  <span className="self-center text-content-muted">{taskPage + 1} / {Math.ceil(tasks.data.total / 50)}</span>
                  <Button size="sm" variant="secondary" disabled={(taskPage + 1) * 50 >= tasks.data.total} onClick={() => setTaskPage(taskPage + 1)}>→</Button>
                </div>
              )}
            </div>
          )}

          {tab === 'texts' && (
            <ol className="space-y-1.5 text-sm list-decimal pl-5 text-content">
              {c.review_texts.map((t, i) => <li key={i}>{t}</li>)}
            </ol>
          )}
        </div>
      )}
    </Modal>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="p-3 rounded-lg bg-surface-muted">
      <div className="text-xs text-content-subtle">{label}</div>
      <div className="text-lg font-semibold text-content tabular-nums">{value}</div>
      {sub && <div className="text-xs text-content-subtle">{sub}</div>}
    </div>
  );
}
